import { R } from './constants';
import { q, type Recorder } from './trace';
import type { Substance, TemperatureClass } from '../model/types';

/** Gas or vapour density at ambient conditions, ρg = pa·M / (R·Ta). */
export function gasDensity(molarMass: number, pressurePa: number, temperatureK: number, rec?: Recorder): number {
  const rho = (pressurePa * molarMass) / (R * temperatureK);
  rec?.step({
    id: 'substance.gas-density',
    label: 'Gas density at ambient conditions',
    ref: { clause: 'B.7.2.3.2' },
    inputs: { pa: q(pressurePa, 'Pa'), M: q(molarMass, 'kg/kmol'), R: q(R, 'J/(kmol·K)'), Ta: q(temperatureK, 'K') },
    output: { symbol: 'ρg', ...q(rho, 'kg/m3') },
  });
  return rho;
}

/**
 * Ratio of specific heats γ. Uses the value given, otherwise derives it for an ideal gas from cp
 * (B.7.2.3.1). Returns undefined when neither is known.
 */
export function polytropicIndex(sub: Substance, rec?: Recorder): number | undefined {
  if (sub.gamma !== undefined) return sub.gamma;
  if (sub.cpJPerKgK === undefined) return undefined;
  const mcp = sub.molarMassKgPerKmol * sub.cpJPerKgK;
  const gamma = mcp / (mcp - R);
  rec?.step({
    id: 'substance.gamma',
    label: 'Ratio of specific heats from cp (ideal gas)',
    ref: { clause: 'B.7.2.3.1' },
    inputs: { M: q(sub.molarMassKgPerKmol, 'kg/kmol'), cp: q(sub.cpJPerKgK, 'J/(kg·K)'), R: q(R, 'J/(kmol·K)') },
    output: { symbol: 'γ', ...q(gamma, '1') },
  });
  return gamma;
}

/**
 * The temperature class a substance needs, from its auto-ignition temperature: the highest class whose
 * maximum surface temperature does not exceed the AIT. AIT = 450 °C gives T1, as for the propane
 * mixture in E.2 Example 4 (MD-009).
 */
export function temperatureClassFor(aitC: number | undefined): TemperatureClass | undefined {
  if (aitC === undefined) return undefined;
  if (aitC >= 450) return 'T1';
  if (aitC >= 300) return 'T2';
  if (aitC >= 200) return 'T3';
  if (aitC >= 135) return 'T4';
  if (aitC >= 100) return 'T5';
  if (aitC >= 85) return 'T6';
  return undefined;
}

/** Maximum surface temperature, °C, for an equipment temperature class. */
export const T_CLASS_MAX_C: Record<TemperatureClass, number> = { T1: 450, T2: 300, T3: 200, T4: 135, T5: 100, T6: 85 };
